/**
 * Product Quick Add To Cart Module - Xử lý việc hiển thị modal thêm nhanh sản phẩm vào giỏ hàng
 * Modal nhỏ gọn hơn Quick View, chỉ chứa variant picker, quantity và action buttons
 */
class ProductQuickAddToCart {
  constructor(options = {}) {
    this.options = {
      modalId: "product-quick-add-to-cart-modal",
      overlayId: "product-quick-add-to-cart-overlay",
      reactContainerId: "product-quick-add-to-cart-react-container",
      ...options,
    };

    this.modal = null;
    this.overlay = null;
    this.reactContainer = null;
    this.isOpen = false;

    this.init();
  }

  init() {
    this.createModal();
    this.bindEvents();
  }

  createModal() {
    // Tạo overlay
    this.overlay = document.createElement("div");
    this.overlay.id = this.options.overlayId;
    this.overlay.className =
      "fixed inset-0 bg-black/50 z-[999] hidden transition-opacity duration-300";

    // Tạo modal
    this.modal = document.createElement("div");
    this.modal.id = this.options.modalId;
    this.modal.className =
      "fixed inset-0 z-[1000] flex items-center justify-center p-4 hidden";

    // Tạo modal content - nhỏ hơn quickview (520px vs 980px)
    const modalContent = document.createElement("div");
    modalContent.className =
      "bg-white rounded-lg shadow-xl max-w-[520px] w-full max-h-[600px] overflow-hidden transform transition-all duration-300 scale-95 opacity-0 flex flex-col";
    modalContent.id = "product-quick-add-to-cart-content";

    // Tạo header với nút đóng
    const header = document.createElement("div");
    header.className =
      "flex items-center justify-between p-4 border-b border-gray-200";
    header.innerHTML = `
      <h3 class="text-base font-semibold text-gray-900">Thêm nhanh vào giỏ hàng</h3>
      <button type="button" class="text-gray-400 hover:text-gray-600 transition-colors" id="close-quick-add-to-cart">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;

    // Tạo body cho React container
    const body = document.createElement("div");
    body.className = "p-4 flex-1 overflow-y-auto";

    this.reactContainer = document.createElement("div");
    this.reactContainer.id = this.options.reactContainerId;
    this.reactContainer.className = "min-h-[150px]";

    body.appendChild(this.reactContainer);
    modalContent.appendChild(header);
    modalContent.appendChild(body);
    this.modal.appendChild(modalContent);

    // Thêm vào DOM
    document.body.appendChild(this.overlay);
    document.body.appendChild(this.modal);
  }

  bindEvents() {
    // Bind click events cho quick add to cart buttons
    document.addEventListener("click", (e) => {
      if (
        e.target.matches(".product-quick-add-to-cart-btn") ||
        e.target.closest(".product-quick-add-to-cart-btn")
      ) {
        e.preventDefault();
        const button = e.target.matches(".product-quick-add-to-cart-btn")
          ? e.target
          : e.target.closest(".product-quick-add-to-cart-btn");
        const productSlug = button.dataset.productSlug;
        const productName = button.dataset.productName;

        if (productSlug) {
          this.openModal(productSlug, productName);
        }
      }

      // Đóng modal khi click nút close
      if (
        e.target.matches("#close-quick-add-to-cart") ||
        e.target.closest("#close-quick-add-to-cart")
      ) {
        e.preventDefault();
        this.closeModal();
      }

      // Đóng modal khi click bên ngoài modal content
      if (
        e.target.id === this.options.overlayId ||
        (e.target.id === this.options.modalId &&
          !e.target.closest("#product-quick-add-to-cart-content"))
      ) {
        this.closeModal();
      }
    });

    // Đóng modal khi nhấn ESC
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.closeModal();
      }
    });
  }

  openModal(productSlug, productName = "") {
    if (this.isOpen) return;

    this.isOpen = true;

    // Hiển thị modal và overlay
    this.overlay.classList.remove("hidden");
    this.modal.classList.remove("hidden");

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Animation
    setTimeout(() => {
      this.overlay.classList.add("opacity-100");
      const content = document.getElementById(
        "product-quick-add-to-cart-content"
      );
      if (content) {
        content.classList.remove("scale-95", "opacity-0");
        content.classList.add("scale-100", "opacity-100");
      }
    }, 10);

    // Load React component với product data
    this.loadReactComponent(productSlug, productName);
  }

  closeModal() {
    if (!this.isOpen) return;

    const content = document.getElementById(
      "product-quick-add-to-cart-content"
    );
    if (content) {
      content.classList.remove("scale-100", "opacity-100");
      content.classList.add("scale-95", "opacity-0");
    }

    this.overlay.classList.remove("opacity-100");

    setTimeout(() => {
      this.overlay.classList.add("hidden");
      this.modal.classList.add("hidden");
      this.isOpen = false;

      // Restore body scroll
      document.body.style.overflow = "";

      // Clear React container
      if (this.reactContainer) {
        this.reactContainer.innerHTML = "";
      }
    }, 300);
  }

  loadReactComponent(productSlug, productName) {
    // Hiển thị loading state
    this.reactContainer.innerHTML = `
      <div class="flex items-center justify-center py-6">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600 text-sm">Đang tải...</span>
      </div>
    `;

    // Set data attributes cho React component
    this.reactContainer.dataset.productSlug = productSlug;
    this.reactContainer.dataset.productName = productName;

    // Thử render React component với retry logic
    const tryRenderReact = (attempts = 0) => {
      if (
        window.ProductQuickAddToCartReact &&
        typeof window.ProductQuickAddToCartReact.render === "function"
      ) {
        window.ProductQuickAddToCartReact.render(this.reactContainer, {
          productSlug: productSlug,
          productName: productName,
        });
      } else if (attempts < 10) {
        // Retry sau 100ms, tối đa 10 lần (1 giây)
        setTimeout(() => tryRenderReact(attempts + 1), 100);
      } else {
        // Fallback nếu React component không load được
        console.error(
          "ProductQuickAddToCartReact failed to load after 10 attempts"
        );
        this.reactContainer.innerHTML = `
          <div class="text-center py-6">
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p class="text-sm text-yellow-800">React component chưa sẵn sàng. Vui lòng thử lại sau.</p>
              <button 
                onclick="window.productQuickAddToCart.closeModal()" 
                class="mt-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
              >
                Đóng
              </button>
            </div>
          </div>
        `;
      }
    };

    // Bắt đầu thử render
    tryRenderReact();
  }

  // Public method để destroy instance
  destroy() {
    if (this.overlay) {
      this.overlay.remove();
    }
    if (this.modal) {
      this.modal.remove();
    }
    this.isOpen = false;
    document.body.style.overflow = "";
  }
}

export default ProductQuickAddToCart;
